import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Offer } from '../interfaces/mock.interface';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { FileCheck, Calendar, DollarSign } from 'lucide-react';

interface LatestOfferTableProps {
  offer: Offer | null;
  maxRows?: number;
}

export const LatestOfferTable = ({ offer, maxRows = 10 }: LatestOfferTableProps) => {
  if (!offer) {
    return (
      <Card className="w-full h-full flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCheck className="h-5 w-5" />
            Ultima Ofertă (Navlu)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            Nu există oferte disponibile.
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (amount: number, currency: string) => {
    const symbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      RON: 'lei',
    };
    const symbol = symbols[currency] || currency;
    return `${symbol}${amount.toLocaleString('en-US')}`;
  };

  // Limit offer elements to maxRows
  const limitedOfferElements = offer.offerElements?.slice(0, maxRows) || [];

  return (
    <Card className="w-full h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <CardTitle className="flex items-center gap-2">
          <FileCheck className="h-5 w-5" />
          Ultima Ofertă (Navlu)
        </CardTitle>
        <div className="flex items-center gap-2 mt-2">
          <Badge variant="default">{offer.type}</Badge>
          <Badge variant="outline">{offer.status}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Offer Details */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Client</p>
            <p className="font-medium">{offer.clientName || 'N/A'}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Terminal</p>
            <p className="font-medium">{offer.terminal}</p>
          </div>
        </div>

        {/* Prices Table */}
        {limitedOfferElements.length > 0 && (
          <div className="pt-2 flex-shrink-0">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Prețuri Navlu
            </h4>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tip Container</TableHead>
                    <TableHead className="text-right">Preț</TableHead>
                    <TableHead>Valută</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {limitedOfferElements.map(element => (
                    <TableRow key={element.id}>
                      <TableCell>
                        <Badge variant="secondary">{element.containerType}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-bold text-lg">
                        {element.price.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{element.currency}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
